import React, { useCallback, forwardRef, useRef } from 'react';
import { useGame } from '../context/GameContext.jsx';
import toastTranslations from '../../data/toast.json';
import { usePinch } from '@use-gesture/react';

const Card = forwardRef(({ card, isMissionCard }, ref) => {
  const { translations, lang, getCardImagePath, formatFieldValue, showToastMessage, zoomStep, setZoomStep } = useGame();

  const contentRef = useRef(null);

  const bind = usePinch(({ offset: [d], event }) => {
    let step = 0;
    if (d >= 2) {
      step = 5;
    } else if (d >= 1.8) {
      step = 4;
    } else if (d >= 1.6) {
      step = 3;
    } else if (d >= 1.4) {
      step = 2;
    } else if (d >= 1.2) {
      step = 1;
    }
    setZoomStep(step);
    event.preventDefault();
  }, { target: contentRef });

  const handleModifierClick = useCallback((modifierKey) => {
    const toastKey = toastTranslations[modifierKey];
    if (toastKey) {
      const message = translations[lang]?.[toastKey];
      if (message) {
        showToastMessage(message);
      } else {
        console.warn(`Translation key '${toastKey}' not found for language '${lang}'.`);
      }
    } else {
      console.warn(`Modifier key '${modifierKey}' not found in toast.json.`);
    }
  }, [translations, lang, showToastMessage]);


  let cardOverlayBadgeHtml = null;
  if (card.Type && card.Type.toLowerCase() === 'clue') {
    let overlayBadgeClass = '';
    switch (card.Category?.toLowerCase()) {
      case 'location': overlayBadgeClass = 'bg-success'; break;
      case 'person': overlayBadgeClass = 'bg-danger'; break;
      case 'evidence': overlayBadgeClass = 'bg-info'; break;
      default: break;
    }
    const cardNumber = card.CardId?.substring(1);
    cardOverlayBadgeHtml = (
      <span className={`badge ${overlayBadgeClass} card-overlay-badge me-2`}>
        {cardNumber}
      </span>
    );
  }

  let costHtml = null;
  if (typeof card.Cost === 'number') {
    const costValue = card.Cost;
    const costBadgeClass = costValue >= 0 ? 'bg-success' : 'bg-danger';
    const costSign = costValue >= 0 ? '+' : '';

    costHtml = (
      <span className={`badge ${costBadgeClass} mission-screen-badge`}>
        <i className="fa-regular fa-hourglass-half me-1"></i> {costSign}{costValue}
      </span>
    );
  }

  const modifiersArray = card.ModifiersLabel ? card.ModifiersLabel.split(';').map(m => m.trim()).filter(m => m !== '') : [];
  const goalsArray = card.GoalsLabel ? card.GoalsLabel.split(';').map(g => g.trim()).filter(g => g !== '') : [];

  const modifierItems = card.Modifiers ? card.Modifiers.map((modifierKey, index) => {
    const displayLabel = modifiersArray[index] || modifierKey;
    return (
      <li key={modifierKey} className="list-inline-item">
        <span
          className="badge bg-warning text-dark mission-screen-badge clickable-investigation-badge"
          onClick={() => handleModifierClick(modifierKey)}
        >
          {displayLabel}
        </span>
      </li>
    );
  }) : [];
  const hasModifiers = modifierItems.length > 0;
  const modifiersHtml = (card.Type && card.Type.toLowerCase() === 'mission' && hasModifiers) ? (
    <div className="text-center mt-4">
      <h5 className="mission-section-title">
        <span className="fw-bold text-white">{translations[lang]?.modifiers_label_full}</span>
        <ul className="list-inline mb-0">
          {modifierItems}
        </ul>
      </h5>
    </div>
  ) : null;


  const goalItems = card.Goals ? card.Goals.map((goalKey, idx) => {
    const displayLabel = goalsArray[idx] || goalKey;
    return (
      <li key={goalKey} className="list-inline-item">
        <span
          className="badge bg-success mission-screen-badge clickable-investigation-badge"
          onClick={() => handleModifierClick(goalKey)}
        >
          {displayLabel}
        </span>
      </li>
    );
  }) : [];
  const hasGoals = goalItems.length > 0;
  const goalsHtml = (card.Type && card.Type.toLowerCase() === 'mission' && hasGoals) ? (
    <div className="text-center mt-3">
      <h5 className="mission-section-title">
        <span className="fw-bold text-white">{translations[lang]?.goals_label_full}</span>
        <ul className="list-inline mb-0">
          {goalItems}
        </ul>
      </h5>
    </div>
  ) : null;

  const processedFieldValue = formatFieldValue(card.FieldValue);
  const cardImageUrl = getCardImagePath(card);
  const shouldDisplayImage = card.Type?.toLowerCase() !== 'clue' || (card.Image && card.Image.trim() !== '');

  return (
    <>
      <div className="card-header-text-container">
        <h2 className="card-title">{cardOverlayBadgeHtml}{card.Title || (translations[lang]?.default_card_title || 'Default Title')}</h2>
        <h4 className="card-subtitle mb-2">{card.Subtitle || ''}</h4>
        <p className="mb-3">
          <span className="badge bg-info mission-screen-badge">{card.CategoryLabel || ''}</span>{' '}
          {card.SubType && <span className="badge bg-secondary ms-2 mission-screen-badge">{card.SubType}</span>}
          {costHtml && <span className="ms-2">{costHtml}</span>}
        </p>
      </div>

      <div className="card-display">
        {shouldDisplayImage && (
          <div className="card-image-wrapper">
            <img ref={ref} src={cardImageUrl} alt={translations[lang]?.mission_card_alt || "Card Image"} className="card-image" />
          </div>
        )}
        <div
          className="card-scrollable-content"
          {...bind}
          ref={contentRef}
          style={{ '--font-increase-step': zoomStep }}
        >
          {card.FieldType === 'text' && processedFieldValue && (
            <div dangerouslySetInnerHTML={{ __html: processedFieldValue }}></div>
          )}
          {modifiersHtml}
          {goalsHtml}
        </div>
      </div>
    </>
  );
});

export default Card;
